import {
  Box,
  Button,
  Drawer,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { format, subHours } from 'date-fns';
import {
  ChangeEvent,
  FormEvent,
  FunctionComponent,
  useMemo,
  useState,
} from 'react';

import { useCreateCarpoolTripMutation } from '../../../../hooks/useCreateCarpoolTripMutation';

interface CreateCarpoolTripDrawerProps {
  eventId: string;
  eventStartAt: Date;
  isOpen: boolean;
  onClose: () => void;
}

interface CreateCarpoolTripForm {
  description: string;
  pickupAt: string;
  pickupExactLocation: string;
  pickupTown: string;
  seats: string;
}

const getInitialForm = (eventStartAt: Date): CreateCarpoolTripForm => ({
  description: '',
  pickupAt: format(subHours(eventStartAt, 3), "yyyy-MM-dd'T'HH:mm"),
  pickupExactLocation: '',
  pickupTown: '',
  seats: '3',
});

export const CreateCarpoolTripDrawer: FunctionComponent<
  CreateCarpoolTripDrawerProps
> = ({ eventId, eventStartAt, isOpen, onClose }) => {
  const { createCarpoolTrip } = useCreateCarpoolTripMutation();
  const [form, setForm] = useState<CreateCarpoolTripForm>(() =>
    getInitialForm(eventStartAt),
  );

  const isSubmitDisabled = useMemo(
    () =>
      !form.pickupAt ||
      !form.pickupExactLocation.trim() ||
      !form.pickupTown.trim() ||
      Number(form.seats) <= 0,
    [form],
  );

  const onChange =
    (field: keyof CreateCarpoolTripForm) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((previousForm) => ({
        ...previousForm,
        [field]: event.target.value,
      }));
    };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    await createCarpoolTrip({
      createCarpoolTripInput: {
        eventId,
        pickupAt: new Date(form.pickupAt),
        pickupExactLocation: form.pickupExactLocation.trim(),
        pickupTown: form.pickupTown.trim(),
        seats: Number(form.seats),
        description: form.description.trim() || null,
      },
    });

    setForm(getInitialForm(eventStartAt));
    onClose();
  };

  const onDrawerClose = () => {
    setForm(getInitialForm(eventStartAt));
    onClose();
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onDrawerClose}>
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{ width: { xs: 360, sm: 440 }, maxWidth: '100vw', p: 3, pt: 11 }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h4">Proposer un trajet</Typography>
            <Typography variant="body2" color="text.secondary">
              Ajoutez les informations utiles pour que les autres participants
              puissent vous rejoindre.
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Ville de départ"
            onChange={onChange('pickupTown')}
            required
            value={form.pickupTown}
          />
          <TextField
            fullWidth
            label="Lieu exact de départ"
            onChange={onChange('pickupExactLocation')}
            required
            value={form.pickupExactLocation}
          />
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Date et heure de départ"
            onChange={onChange('pickupAt')}
            required
            type="datetime-local"
            value={form.pickupAt}
          />
          <TextField
            fullWidth
            inputProps={{ min: 1 }}
            label="Nombre de places"
            onChange={onChange('seats')}
            required
            type="number"
            value={form.seats}
          />
          <TextField
            fullWidth
            label="Description"
            minRows={4}
            multiline
            onChange={onChange('description')}
            placeholder="Exemple : coffre disponible, détour possible, départ après 18h..."
            value={form.description}
          />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button color="inherit" onClick={onDrawerClose}>
              Annuler
            </Button>
            <Button
              disabled={isSubmitDisabled}
              type="submit"
              variant="contained"
            >
              Publier le trajet
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};
